<template>
  <div class="sales-tab">
    <div class="sales-filters">
      <div class="sales-filters__left">
        <v-text-field
          v-model="search"
          :placeholder="$t('sales_search_placeholder')"
          solo
          flat
          dense
          hide-details
          background-color="#F6F6F6"
          class="pkmn-input sales-search"
        >
          <template v-slot:append>
            <v-icon class="sales-search__icon" dense>mdi-magnify</v-icon>
          </template>
        </v-text-field>

        <div class="sales-select">
          <div class="sales-select__label">{{ $t('sales_search_by') }}</div>
          <v-select
            v-model="searchBy"
            :items="searchOptions"
            item-text="text"
            item-value="value"
            solo
            flat
            dense
            hide-details
            background-color="#F6F6F6"
            class="pkmn-input"
            append-icon="mdi-chevron-down"
          />
        </div>
      </div>

      <div class="sales-filters__right">
        <div class="sales-period-picker">
          <period-picker
            :max-range-days="31"
            @periodUpdated="handlePeriodUpdated"
          />
        </div>
        <div class="sales-export-wrap">
          <v-btn
            class="pkmn-button-small sales-export"
            :class="{ 'sales-export--compact': exportIsCompact }"
            color="primary"
            @click="handleExport"
          >
            {{ exportLabel }}
          </v-btn>
        </div>
      </div>
    </div>

    <data-table-sales
      :data="data"
      :search="search"
      :search-by="searchBy"
      @selectionChanged="handleSelection"
    />
  </div>
</template>

<script>
import PeriodPicker from '@/components/Common/PeriodPicker.vue'
import DataTableSales from '@/components/Merchant/DataTable/DataTableSales.vue'

export default {
  name: 'MerchantSales',
  components: {
    PeriodPicker,
    DataTableSales
  },
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      search: '',
      searchBy: 'location_name',
      selectedCount: 0,
      selectedItems: [],
      periodStartDate: null,
      periodEndDate: null
    }
  },
  computed: {
    searchOptions() {
      return [
        {
          text: this.$t('sales_search_by_location_name'),
          value: 'location_name'
        },
        {
          text: this.$t('sales_search_by_location_code'),
          value: 'location_code'
        },
        { text: this.$t('sales_search_by_location_id'), value: 'location_id' }
      ]
    },
    exportLabel() {
      return this.selectedCount > 0
        ? this.$t('sales_export_data_count', { count: this.selectedCount })
        : this.$t('sales_export_data')
    },
    exportIsCompact() {
      return this.exportLabel.length > 16
    }
  },
  watch: {
    search(value) {
      this.$emit('searchUpdated', value)
    },
    searchBy(value) {
      this.$emit('searchByUpdated', value)
    }
  },
  methods: {
    handleSelection(selected) {
      this.selectedItems = selected
      this.selectedCount = selected.length
    },
    handlePeriodUpdated(event) {
      this.periodStartDate = event.startDate
      this.periodEndDate = event.endDate
      this.$emit('periodUpdated', event)
    },
    handleExport() {
      if (this.selectedCount === 0) {
        this.$store.dispatch('setSystemMessage', {
          type: 'error',
          message: this.$t('sales_export_no_selection')
        })
        return
      }

      this.$emit('export', {
        selectedItems: this.selectedItems,
        startDate: this.periodStartDate,
        endDate: this.periodEndDate
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.sales-tab {
  display: flex;
  flex-direction: column;
}

.sales-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-bottom;
  justify-content: space-between;
  gap: 16px;
  margin: 20px 0 8px;
}

.sales-filters__left {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
}

.sales-filters__right {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.sales-search {
  min-width: 220px;
  max-width: 320px;
}

.sales-search ::v-deep .v-input__slot {
  min-height: 24px;
  height: 24px;
}

.sales-search ::v-deep input {
  height: 24px;
  line-height: 24px;
  padding-top: 0;
  padding-bottom: 0;
}

.sales-search__icon {
  color: rgba(31, 31, 31, 0.4);
}

.sales-search ::v-deep .v-input__append-inner {
  padding-right: 6px;
}

.sales-select {
  min-width: 180px;
}

.sales-select__label {
  color: rgba(31, 31, 31, 0.5);
  font-family: Roboto;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  margin-bottom: 4px;
}

.sales-select ::v-deep .v-input__slot {
  min-height: 24px;
  height: 24px;
}

.sales-select ::v-deep .v-select__selections {
  min-height: 24px;
  height: 24px;
  align-items: center;
}

.sales-select ::v-deep .v-select__selection {
  line-height: 24px;
}

.sales-export {
  height: 24px;
  width: 112px;
  min-width: 112px;
  padding: 0 12px;
}

.sales-period-picker ::v-deep .my-button {
  height: 24px;
  min-height: 24px;
  line-height: 24px;
  padding: 0 8px;
  font-size: 12px;
}

.sales-export ::v-deep .v-btn__content {
  color: #fff;
  white-space: nowrap;
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%;
}

.sales-export--compact ::v-deep .v-btn__content {
  font-size: 11px;
  letter-spacing: -0.2px;
}

.sales-export-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.sales-export-error {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fff3f3;
  color: #eb5757;
  font-size: 11px;
  line-height: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.sales-export-error__icon {
  color: #eb5757;
}

.sales-export-error__close {
  color: #b9b9b9;
  cursor: pointer;
}
</style>
